import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	Button,
	Card,
	CardContent,
	Checkbox,
	CircularProgress,
	Grid,
	Typography,
} from '@mui/material';
import styles from './SchedulerSetting.module.scss';
import {
	buildLogbookSalesMetricPreferenceMap,
	fetchLogbookSalesMetricPreferences,
	fetchLogbookSalesMetrics,
	LogbookSalesMetric,
	upsertLogbookSalesMetricPreference,
} from 'core/API/logbookSalesMetrics';
import { useToastContext } from 'core/components/Toast/context/ToastContext';

type MetricRow = LogbookSalesMetric & {
	preferenceExists: boolean;
	preferenceActive: boolean | null;
	checked: boolean;
};

const LogbooksSetting = () => {
	const queryClient = useQueryClient();
	const { workplaceId } = useWorkplacesContext();
	const workplaceIdNum = Number(workplaceId);
	const { showToast } = useToastContext();
	const [saving, setSaving] = useState(false);
	const [localChecked, setLocalChecked] = useState<Record<number, boolean>>({});

	const {
		data: masterMetrics,
		isLoading: isMasterLoading,
		isError: isMasterError,
	} = useQuery(['logbook-sales-metrics'], fetchLogbookSalesMetrics, {
		enabled: true,
		retry: false,
		refetchOnWindowFocus: false,
		staleTime: 5 * 60 * 1000,
	});

	const {
		data: preferences,
		isLoading: isPreferencesLoading,
		isError: isPreferencesError,
	} = useQuery(
		['logbook-sales-metric-preferences', workplaceIdNum],
		() => fetchLogbookSalesMetricPreferences(workplaceIdNum),
		{
			enabled: !!workplaceIdNum,
			retry: false,
			refetchOnWindowFocus: false,
		}
	);

	const derivedRows = useMemo<MetricRow[]>(() => {
		if (!masterMetrics) return [];
		const prefMap = buildLogbookSalesMetricPreferenceMap(preferences);
		return masterMetrics.map((m) => {
			const pref = prefMap.get(m.id);
			const preferenceExists = pref != null;
			const preferenceActive = preferenceExists ? pref!.active : null;
			const checked = preferenceExists ? Boolean(preferenceActive) : false;
			return { ...m, preferenceExists, preferenceActive, checked };
		});
	}, [masterMetrics, preferences]);

	useEffect(() => {
		const initial: Record<number, boolean> = {};
		derivedRows.forEach((r) => { initial[r.id] = r.checked; });
		setLocalChecked(initial);
	}, [derivedRows]);

	const isDirty = useMemo(
		() => derivedRows.some((r) => (localChecked[r.id] ?? r.checked) !== r.checked),
		[derivedRows, localChecked]
	);

	const categories = useMemo(() => {
		const seen = new Set<string>();
		const ordered: string[] = [];
		derivedRows.forEach((r) => {
			const cat = r.category ?? '';
			if (!seen.has(cat)) { seen.add(cat); ordered.push(cat); }
		});
		return ordered;
	}, [derivedRows]);

	const disabledMetricNames = ['sales', 'customer count by day part'];

	const isMetricDisabled = (metricName: string): boolean =>
		disabledMetricNames.some(
			(d) => metricName.toLowerCase().trim() === d.toLowerCase().trim()
		);

	const getDisplayName = (metric: LogbookSalesMetric): string => {
		let displayName = metric.name;
		if (metric.metricType === 'DAY_PART' && !displayName.toLowerCase().includes('by day part')) {
			displayName = `${displayName} by day part`;
		}
		return displayName;
	};

	const handleToggle = (metricId: number) => {
		setLocalChecked((prev) => ({ ...prev, [metricId]: !prev[metricId] }));
	};

	const handleSave = async () => {
		if (!workplaceIdNum) return;
		setSaving(true);
		try {
			await Promise.all(
				derivedRows
					.filter((r) => (localChecked[r.id] ?? r.checked) !== r.checked)
					.map((r) =>
						upsertLogbookSalesMetricPreference({
							workplaceId: workplaceIdNum,
							metricId: r.id,
							active: localChecked[r.id],
						})
					)
			);
			await queryClient.invalidateQueries(['logbook-sales-metric-preferences', workplaceIdNum]);
			showToast('success', 'Logbook metric preferences saved');
		} catch (err) {
			console.error('Failed to save metric preferences', err);
			showToast('error', 'Failed to save metric preferences');
		} finally {
			setSaving(false);
		}
	};

	const isBusy = isMasterLoading || (isPreferencesLoading && !!workplaceIdNum);

	return (
		<Card sx={{ height: '100%' }}>
			<CardContent>
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid item>
						<Typography variant="h6" sx={{ fontFamily: 'Gotham', fontSize: '20px' }}>
							Logbook Sales Metrics
						</Typography>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							sx={{ backgroundColor: '#446388' }}
							disabled={!isDirty || isBusy || saving}
							onClick={handleSave}
						>
							{saving ? <CircularProgress size={20} /> : 'Save'}
						</Button>
					</Grid>
				</Grid>

				{isBusy ? (
					<Grid container justifyContent="center" sx={{ mt: 4 }}>
						<CircularProgress size={20} color="inherit" />
					</Grid>
				) : isMasterError || isPreferencesError ? (
					<Typography color="error" sx={{ mt: 4, pl: 2 }}>
						{isMasterError
							? 'Failed to load sales metrics. Please refresh the page.'
							: 'Failed to load metric preferences. Please check your workplace selection.'}
					</Typography>
				) : (
					<div className={styles.tableContainer} style={{ marginTop: 16 }}>
						<table className={styles.workplaceTable}>
							<thead>
								<tr>
									<th>Metric Name</th>
									<th style={{ width: 96, textAlign: 'center' }}>Enabled</th>
								</tr>
							</thead>
							<tbody>
								{derivedRows.length === 0 ? (
									<tr>
										<td colSpan={2} style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
											No records found
										</td>
									</tr>
								) : (
									categories.map((cat) => {
										const catRows = derivedRows.filter((r) => (r.category ?? '') === cat);
										return (
											<React.Fragment key={cat}>
												{cat && (
													<tr>
														<td
															colSpan={2}
															style={{
																fontWeight: 600,
																fontFamily: 'Gotham',
																backgroundColor: '#f0f4f8',
																color: '#446388',
																padding: '8px 10px',
															}}
														>
															{cat}
														</td>
													</tr>
												)}
												{catRows.map((r) => {
													const displayName = getDisplayName(r);
													const disabled = isMetricDisabled(r.name) || saving;
													return (
														<tr key={r.id}>
															<td style={{ fontFamily: 'Gotham' }}>{displayName}</td>
															<td style={{ textAlign: 'center', width: 96 }}>
																<Checkbox
																	checked={localChecked[r.id] ?? r.checked}
																	disabled={disabled}
																	onChange={() => handleToggle(r.id)}
																	title={
																		disabled
																			? 'This metric is automatically enabled and cannot be disabled'
																			: ''
																	}
																/>
															</td>
														</tr>
													);
												})}
											</React.Fragment>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

export default LogbooksSetting;
